import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Table,
    Card,
    Typography,
    Tag,
    Input,
    Select,
    Button,
    Space,
    Modal,
    Form,
    DatePicker,
    Steps,
    Descriptions,
    Avatar,
    Empty,
    Divider,
    message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined, ReloadOutlined, SwapOutlined, EyeOutlined, LinkOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axiosInstance from '../../../core/services/axiosConfig';
import { apis } from '../../../core/services/params';
import { toProductImageUrl } from './utils';
import {
    ClaimOutcome,
    ClaimStage,
    ClaimStageTransitionRequest,
    CLAIM_STAGE_COLORS,
    CLAIM_STAGE_LABELS,
    ResponseModel,
    ServiceMode,
    TERMINAL_CLAIM_STAGES,
    UserServiceWarrantyDTO,
    WarrantyClaimDTO,
    WarrantyStatus,
} from './types';

const { Text } = Typography;
const { TextArea } = Input;

const formatDate = (value?: string) => (value ? dayjs(value).format('DD MMM YYYY') : '-');
const formatDateTime = (value?: string) => (value ? dayjs(value).format('DD MMM YYYY, hh:mm A') : '-');

// Claimed warranties + the claims raised against them used to be two separate
// tabs joined manually by warrantyId - the backend now returns claims inline
// on each warranty (see UserServiceWarrantyDTO.claims), so this single view
// covers both: a dense summary table plus the full claim timeline/move-stage
// workflow inside the detail modal.
const ClaimedWarrantiesList = () => {
    const { user } = useSelector((s: any) => s.authStore);
    const updatedBy: string = user?.email || String(user?.id || 'cms-user');

    const [items, setItems] = useState<UserServiceWarrantyDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState<WarrantyStatus | undefined>(undefined);
    const [claimStageFilter, setClaimStageFilter] = useState<ClaimStage | undefined>(undefined);

    const [detailVisible, setDetailVisible] = useState(false);
    const [selectedWarranty, setSelectedWarranty] = useState<UserServiceWarrantyDTO | null>(null);

    const [moveModalVisible, setMoveModalVisible] = useState(false);
    const [selectedClaim, setSelectedClaim] = useState<WarrantyClaimDTO | null>(null);
    const [nextStages, setNextStages] = useState<ClaimStage[]>([]);
    const [moveLoading, setMoveLoading] = useState(false);
    const [form] = Form.useForm();
    const toStageWatch: ClaimStage | undefined = Form.useWatch('toStage', form);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get<ResponseModel<UserServiceWarrantyDTO[]>>(
                apis.GET_CLAIMED_WARRANTIES
            );
            if (response.data?.status) {
                setItems(response.data.data || []);
            } else {
                message.error(response.data?.statusDescription || 'Failed to fetch claimed warranties');
            }
        } catch (error: any) {
            console.error('Error fetching claimed warranties:', error);
            message.error(error?.response?.data?.statusDescription || 'Failed to fetch claimed warranties');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
    }, []);

    const openDetail = (record: UserServiceWarrantyDTO) => {
        setSelectedWarranty(record);
        setDetailVisible(true);
    };

    const openMoveModal = async (claim: WarrantyClaimDTO) => {
        try {
            const response = await axiosInstance.get<ResponseModel<ClaimStage[]>>(
                `${apis.GET_WARRANTY_CLAIM_NEXT_STAGES}${claim.ticketId}/next-stages`
            );
            if (response.data?.status) {
                setSelectedClaim(claim);
                setNextStages(response.data.data || []);
                form.resetFields();
                setMoveModalVisible(true);
            } else {
                message.error(response.data?.statusDescription || 'Failed to load next stages');
            }
        } catch (error: any) {
            message.error(error?.response?.data?.statusDescription || 'Failed to load next stages');
        }
    };

    // Patches the moved claim into both the open detail modal and the table's
    // cached items, so the UI reflects the move immediately without a refetch.
    const applyClaimUpdate = (updatedClaim: WarrantyClaimDTO) => {
        setSelectedWarranty((prev) =>
            prev
                ? { ...prev, claims: (prev.claims || []).map((c) => (c.ticketId === updatedClaim.ticketId ? updatedClaim : c)) }
                : prev
        );
        setItems((prev) =>
            prev.map((warranty) =>
                warranty.claims?.some((c) => c.ticketId === updatedClaim.ticketId)
                    ? { ...warranty, claims: warranty.claims!.map((c) => (c.ticketId === updatedClaim.ticketId ? updatedClaim : c)) }
                    : warranty
            )
        );
    };

    const handleMoveStage = async (values: any) => {
        if (!selectedClaim) return;
        setMoveLoading(true);
        try {
            const payload: ClaimStageTransitionRequest = {
                toStage: values.toStage,
                comment: values.comment,
                updatedBy,
            };

            if (values.toStage === ClaimStage.UNDER_BRAND_REVIEW && values.expectedResolutionDate) {
                payload.expectedResolutionDate = values.expectedResolutionDate.valueOf();
            }

            if (values.toStage === ClaimStage.OUTCOME_DECLARED) {
                payload.outcome = values.outcome;
            }

            if (values.toStage === ClaimStage.PICKUP_SCHEDULED || values.toStage === ClaimStage.VISIT_SCHEDULED) {
                if (values.visitMode || values.visitDate || values.visitSlot || values.visitAddress) {
                    payload.scheduledVisit = {
                        mode: values.visitMode,
                        visitDate: values.visitDate ? String(values.visitDate.valueOf()) : undefined,
                        slot: values.visitSlot,
                        address: values.visitAddress,
                    };
                }
            }

            const response = await axiosInstance.post<ResponseModel<WarrantyClaimDTO>>(
                `${apis.MOVE_WARRANTY_CLAIM_STAGE}${selectedClaim.ticketId}/stage`,
                payload
            );

            if (response.data?.status) {
                const updatedClaim = response.data.data;
                message.success(`Claim moved to ${CLAIM_STAGE_LABELS[updatedClaim.stage]}`);
                applyClaimUpdate(updatedClaim);
                setMoveModalVisible(false);
            } else {
                message.error(response.data?.statusDescription || 'Failed to move claim stage');
            }
        } catch (error: any) {
            message.error(error?.response?.data?.statusDescription || 'Failed to move claim stage');
        } finally {
            setMoveLoading(false);
        }
    };

    const filteredItems = items.filter((warranty) => {
        const search = searchText.trim().toLowerCase();
        const matchesSearch =
            !search ||
            warranty.warrantyId.toLowerCase().includes(search) ||
            warranty.productId.toLowerCase().includes(search) ||
            warranty.userName?.toLowerCase().includes(search) ||
            warranty.userPhone?.toLowerCase().includes(search) ||
            warranty.userEmail?.toLowerCase().includes(search) ||
            String(warranty.orderId).includes(search) ||
            warranty.claims?.some((c) => c.ticketId.toLowerCase().includes(search));
        const matchesStatus = !statusFilter || warranty.status === statusFilter;
        const matchesClaimStage = !claimStageFilter || warranty.claims?.some((c) => c.stage === claimStageFilter);
        return matchesSearch && matchesStatus && matchesClaimStage;
    });

    const columns: ColumnsType<UserServiceWarrantyDTO> = [
        {
            title: 'Product',
            key: 'product',
            width: 240,
            render: (_, record) => (
                <Space align="start">
                    <Avatar
                        shape="square"
                        src={toProductImageUrl(record.productInfo?.mainImageLink) || record.productImage}
                        size={44}
                        style={{ border: '1px solid #f0f0f0', flexShrink: 0 }}
                    >
                        {record.productId?.slice(0, 2)}
                    </Avatar>
                    <div>
                        <Text strong>{record.productName || record.productId}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {record.productInfo?.brandName || 'Brand unknown'}
                            {record.variant ? ` · ${record.variant}` : ''}
                        </Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {record.productInfo?.categoryName || record.productInfo?.categoryNames?.[0] || 'Category unknown'}
                        </Text>
                    </div>
                </Space>
            ),
        },
        {
            title: 'Warranty',
            key: 'warranty',
            width: 200,
            render: (_, record) => (
                <>
                    <Text strong>{record.serviceId}</Text>
                    <br />
                    <Text style={{ fontSize: 12 }}>
                        {formatDate(record.issuedAt)} → {formatDate(record.expiresAt)}
                    </Text>
                    <br />
                    <Tag color={record.status === 'ACTIVE' ? 'green' : 'red'} style={{ marginTop: 4 }}>
                        {record.status}
                    </Tag>
                    {record.status === 'ACTIVE' && (
                        <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
                            {record.daysLeftToExpire}d left
                        </Text>
                    )}
                </>
            ),
        },
        {
            title: 'Order & Customer',
            key: 'orderCustomer',
            width: 220,
            render: (_, record) => (
                <>
                    <Text strong>Order {record.orderId}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}> / Item {record.orderItemId}</Text>
                    <br />
                    <Text style={{ fontSize: 12 }}>{record.userName || `User ${record.userId}`}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {record.userPhone || record.userEmail || '-'}
                    </Text>
                    {record.amountPaid != null && (
                        <>
                            <br />
                            <Text style={{ fontSize: 12 }}>₹{record.amountPaid}</Text>
                        </>
                    )}
                    {record.invoiceUrl && (
                        <>
                            {' '}
                            <a href={record.invoiceUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>
                                <LinkOutlined /> Invoice
                            </a>
                        </>
                    )}
                </>
            ),
        },
        {
            title: 'Claims',
            key: 'claims',
            width: 220,
            render: (_, record) => {
                if (!record.claims || record.claims.length === 0) {
                    return <Text type="secondary">No claims</Text>;
                }
                return (
                    <Space size={[4, 4]} wrap>
                        {record.claims.map((claim) => (
                            <Tag
                                key={claim.ticketId}
                                color={CLAIM_STAGE_COLORS[claim.stage]}
                                style={{ cursor: 'pointer', marginRight: 0 }}
                                onClick={() => openDetail(record)}
                            >
                                {claim.ticketId} · {CLAIM_STAGE_LABELS[claim.stage]}
                            </Tag>
                        ))}
                    </Space>
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 90,
            render: (_, record) => (
                <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => openDetail(record)} style={{ padding: 0, height: 'auto' }}>
                    View
                </Button>
            ),
        },
    ];

    return (
        <div style={{ padding: '16px 20px' }}>
            <div style={{ marginBottom: 12 }}>
                <Text strong style={{ fontSize: 16 }}>
                    Claimed Warranties ({filteredItems.length})
                </Text>
            </div>

            <Space style={{ marginBottom: 16 }} wrap>
                <Input
                    placeholder="Search warranty, product, ticket, order or customer"
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: 320 }}
                    allowClear
                />
                <Select
                    placeholder="Filter by warranty status"
                    style={{ width: 200 }}
                    allowClear
                    value={statusFilter}
                    onChange={setStatusFilter}
                    options={[
                        { label: 'Active', value: 'ACTIVE' },
                        { label: 'Expired', value: 'EXPIRED' },
                    ]}
                />
                <Select
                    placeholder="Filter by claim stage"
                    style={{ width: 220 }}
                    allowClear
                    value={claimStageFilter}
                    onChange={setClaimStageFilter}
                    options={Object.values(ClaimStage).map((stage) => ({
                        label: CLAIM_STAGE_LABELS[stage],
                        value: stage,
                    }))}
                />
                <Button icon={<ReloadOutlined />} onClick={fetchItems}>
                    Refresh
                </Button>
            </Space>

            <Card style={{ overflow: 'hidden' }}>
                <Table<UserServiceWarrantyDTO>
                    rowKey="warrantyId"
                    columns={columns}
                    dataSource={filteredItems}
                    loading={loading}
                    scroll={{ x: 1100 }}
                    pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'] }}
                    size="small"
                />
            </Card>

            <Modal
                title={selectedWarranty?.warrantyId}
                open={detailVisible}
                onCancel={() => setDetailVisible(false)}
                footer={null}
                width={720}
            >
                {selectedWarranty && (
                    <>
                        <Descriptions column={2} size="small" bordered style={{ marginBottom: 20 }}>
                            <Descriptions.Item label="Product" span={2}>
                                <Space>
                                    <Avatar
                                        shape="square"
                                        src={toProductImageUrl(selectedWarranty.productInfo?.mainImageLink) || selectedWarranty.productImage}
                                        size={40}
                                    >
                                        {selectedWarranty.productId?.slice(0, 2)}
                                    </Avatar>
                                    <div>
                                        <Text strong>{selectedWarranty.productName || selectedWarranty.productId}</Text>
                                        <br />
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {selectedWarranty.productInfo?.brandName} · {selectedWarranty.productInfo?.categoryName}
                                        </Text>
                                    </div>
                                </Space>
                            </Descriptions.Item>
                            <Descriptions.Item label="Service ID">{selectedWarranty.serviceId}</Descriptions.Item>
                            <Descriptions.Item label="Status">
                                <Tag color={selectedWarranty.status === 'ACTIVE' ? 'green' : 'red'}>{selectedWarranty.status}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Issued">{formatDate(selectedWarranty.issuedAt)}</Descriptions.Item>
                            <Descriptions.Item label="Expires">
                                {formatDate(selectedWarranty.expiresAt)} ({selectedWarranty.daysLeftToExpire}d left)
                            </Descriptions.Item>
                            <Descriptions.Item label="Order">
                                {selectedWarranty.orderId} / Item {selectedWarranty.orderItemId}
                            </Descriptions.Item>
                            <Descriptions.Item label="Amount Paid">
                                {selectedWarranty.amountPaid != null ? `₹${selectedWarranty.amountPaid}` : '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Customer" span={2}>
                                {selectedWarranty.userName || `User ${selectedWarranty.userId}`}
                                {selectedWarranty.userPhone ? ` · ${selectedWarranty.userPhone}` : ''}
                                {selectedWarranty.userEmail ? ` · ${selectedWarranty.userEmail}` : ''}
                            </Descriptions.Item>
                            {selectedWarranty.invoiceUrl && (
                                <Descriptions.Item label="Invoice" span={2}>
                                    <a href={selectedWarranty.invoiceUrl} target="_blank" rel="noreferrer">
                                        <LinkOutlined /> View Invoice
                                    </a>
                                </Descriptions.Item>
                            )}
                        </Descriptions>

                        <Divider titlePlacement="left" plain>
                            Claims ({selectedWarranty.claims?.length || 0})
                        </Divider>

                        {!selectedWarranty.claims || selectedWarranty.claims.length === 0 ? (
                            <Empty description="No claims raised against this warranty yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        ) : (
                            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                {selectedWarranty.claims.map((claim) => (
                                    <Card
                                        key={claim.ticketId}
                                        size="small"
                                        title={
                                            <Space>
                                                <Text strong>{claim.ticketId}</Text>
                                                <Tag color={CLAIM_STAGE_COLORS[claim.stage]}>{CLAIM_STAGE_LABELS[claim.stage]}</Tag>
                                            </Space>
                                        }
                                        extra={
                                            !TERMINAL_CLAIM_STAGES.has(claim.stage) && (
                                                <Button size="small" type="primary" icon={<SwapOutlined />} onClick={() => openMoveModal(claim)}>
                                                    Move Stage
                                                </Button>
                                            )
                                        }
                                    >
                                        <Descriptions column={1} size="small" style={{ marginBottom: 12 }}>
                                            <Descriptions.Item label="Issue Type">{claim.issueType}</Descriptions.Item>
                                            {claim.issueDescription && (
                                                <Descriptions.Item label="Issue Description">{claim.issueDescription}</Descriptions.Item>
                                            )}
                                            {claim.expectedResolutionDate && (
                                                <Descriptions.Item label="Expected By">
                                                    {formatDateTime(claim.expectedResolutionDate)}
                                                </Descriptions.Item>
                                            )}
                                            {claim.outcome && <Descriptions.Item label="Outcome">{claim.outcome}</Descriptions.Item>}
                                            {claim.scheduledVisit && (
                                                <Descriptions.Item label="Scheduled Visit">
                                                    {claim.scheduledVisit.mode} · {claim.scheduledVisit.slot || '-'} ·{' '}
                                                    {claim.scheduledVisit.address || '-'}
                                                </Descriptions.Item>
                                            )}
                                            <Descriptions.Item label="Updated">
                                                {formatDateTime(claim.updatedAt)} {claim.updatedBy ? `by ${claim.updatedBy}` : ''}
                                            </Descriptions.Item>
                                        </Descriptions>

                                        <Steps
                                            direction="vertical"
                                            size="small"
                                            current={claim.timeline.length}
                                            items={claim.timeline.map((event) => ({
                                                title: CLAIM_STAGE_LABELS[event.stage],
                                                description: (
                                                    <>
                                                        {event.note && <div>{event.note}</div>}
                                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                                            {formatDateTime(event.occurredAt)}
                                                        </Text>
                                                    </>
                                                ),
                                                status: 'finish',
                                            }))}
                                        />
                                    </Card>
                                ))}
                            </Space>
                        )}
                    </>
                )}
            </Modal>

            <Modal
                title={`Move ${selectedClaim?.ticketId} to next stage`}
                open={moveModalVisible}
                onCancel={() => setMoveModalVisible(false)}
                onOk={() => form.submit()}
                confirmLoading={moveLoading}
                okText="Move Stage"
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleMoveStage}>
                    <Form.Item
                        name="toStage"
                        label="Move to stage"
                        rules={[{ required: true, message: 'Please select the next stage' }]}
                    >
                        <Select
                            placeholder="Select next stage"
                            options={nextStages.map((stage) => ({ label: CLAIM_STAGE_LABELS[stage], value: stage }))}
                        />
                    </Form.Item>

                    <Form.Item
                        name="comment"
                        label="Comment"
                        rules={[{ required: true, message: 'A comment is required for every stage move' }]}
                    >
                        <TextArea rows={3} placeholder="Why is this claim moving to this stage?" />
                    </Form.Item>

                    {toStageWatch === ClaimStage.UNDER_BRAND_REVIEW && (
                        <Form.Item name="expectedResolutionDate" label="Expected resolution date (optional)">
                            <DatePicker style={{ width: '100%' }} showTime />
                        </Form.Item>
                    )}

                    {toStageWatch === ClaimStage.OUTCOME_DECLARED && (
                        <Form.Item
                            name="outcome"
                            label="Outcome"
                            rules={[{ required: true, message: 'Outcome is required for this stage' }]}
                        >
                            <Select
                                placeholder="Select outcome"
                                options={Object.values(ClaimOutcome).map((outcome) => ({
                                    label: outcome.replace('_', ' '),
                                    value: outcome,
                                }))}
                            />
                        </Form.Item>
                    )}

                    {(toStageWatch === ClaimStage.PICKUP_SCHEDULED || toStageWatch === ClaimStage.VISIT_SCHEDULED) && (
                        <>
                            <Form.Item name="visitMode" label="Service mode (optional)">
                                <Select
                                    allowClear
                                    placeholder="Select mode"
                                    options={Object.values(ServiceMode).map((mode) => ({ label: mode, value: mode }))}
                                />
                            </Form.Item>
                            <Form.Item name="visitDate" label="Visit date (optional)">
                                <DatePicker style={{ width: '100%' }} showTime />
                            </Form.Item>
                            <Form.Item name="visitSlot" label="Slot (optional)">
                                <Input placeholder="e.g. 10 AM - 12 PM" />
                            </Form.Item>
                            <Form.Item name="visitAddress" label="Address (optional)">
                                <Input placeholder="Pickup/visit address" />
                            </Form.Item>
                        </>
                    )}
                </Form>
            </Modal>
        </div>
    );
};

export default ClaimedWarrantiesList;
